/**
 * AI 계약서 초안 페이지 (/contract-ai/:roomId) — tech_spec.md 8-1절
 * 채팅 로그에서 조건을 추출해 SW프리랜서 표준계약서 초안을 생성. 건당 490원 / 12건 4,990원(contract 이용권).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FileText, ArrowLeft, AlertTriangle, RefreshCw, Printer, ExternalLink, Download } from 'lucide-react';
import { contractAiAPI, ContractDraftExtracted, ContractDraftTemplate } from '../api/contractAiAPI';
import { aiCreditsAPI } from '../api/aiCreditsAPI';
import AICreditGate from '../components/AICreditGate';
import { downloadAuthedFile } from '../utils/downloadFile';

const S = {
  page: { minHeight: '100vh', background: '#080808', color: '#fff', fontFamily: "-apple-system,'Pretendard',sans-serif", padding: '32px 24px' } as React.CSSProperties,
  container: { maxWidth: 680, margin: '0 auto' } as React.CSSProperties,
  card: { background: '#0d0d0d', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16, padding: 24, marginBottom: 16 } as React.CSSProperties,
};

// 생성 중 단계별 안내 문구 — 실제 처리 단계와 대략 맞춰 순서대로 보여줌(체감 대기시간 개선)
const GENERATE_STAGES = [
  '채팅 내용을 확인하고 있어요...',
  '계약 조건(업무 범위·금액·기간)을 추출하고 있어요...',
  '표준계약서 양식에 채우는 중이에요...',
  '마무리하는 중이에요...',
];

const AIContractDraftPage: React.FC = () => {
  const { roomId } = useParams<{ roomId: string }>();
  const navigate = useNavigate();
  const [credits, setCredits] = useState(0);
  const [draftId, setDraftId] = useState<number | null>(null);
  const [extracted, setExtracted] = useState<ContractDraftExtracted | null>(null);
  const [template, setTemplate] = useState<ContractDraftTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [stageIndex, setStageIndex] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [err, setErr] = useState('');

  // 생성 중일 때 1.4초마다 다음 안내 문구로 전환 (마지막 문구에서 대기)
  useEffect(() => {
    if (!generating) { setStageIndex(0); return; }
    const timer = setInterval(() => {
      setStageIndex(i => Math.min(i + 1, GENERATE_STAGES.length - 1));
    }, 1400);
    return () => clearInterval(timer);
  }, [generating]);

  const load = useCallback(async () => {
    if (!roomId) return;
    setLoading(true);
    try {
      const [bal, latest] = await Promise.all([
        aiCreditsAPI.getBalance('contract'),
        contractAiAPI.getLatest(Number(roomId)),
      ]);
      setCredits(bal);
      if (latest.success && latest.data) {
        setDraftId(latest.data.draftId);
        setExtracted(latest.data.extracted);
        setTemplate(latest.data.template);
      }
    } catch (e: any) {
      setErr(e.response?.data?.message || '조회 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, [roomId]);

  useEffect(() => { load(); }, [load]);

  const handleGenerate = async () => {
    if (!roomId) return;
    setGenerating(true);
    setErr('');
    try {
      const res = await contractAiAPI.generate(Number(roomId));
      if (!res.success) {
        setErr(res.message || '생성에 실패했습니다.');
        if (res.code === 'NO_CREDIT') setCredits(0);
        return;
      }
      setDraftId(res.data!.draftId);
      setExtracted(res.data!.extracted);
      setTemplate(res.data!.template);
      setCredits(await aiCreditsAPI.getBalance('contract'));
    } catch (e: any) {
      setErr(e.response?.data?.message || '생성 중 오류가 발생했습니다.');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async () => {
    if (!draftId) return;
    setDownloading(true);
    try {
      await downloadAuthedFile(`/api/contract-ai/download/${draftId}`, `AI-contract-draft-${roomId}.docx`);
    } catch (e: any) {
      setErr(e.message || '다운로드 중 오류가 발생했습니다.');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return <div style={{ ...S.page, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <RefreshCw size={24} color="#a78bfa" style={{ animation: 'spin 1s linear infinite' }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>;
  }

  return (
    <div style={S.page} className="ai-contract-draft-page">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .ai-contract-draft-page { background: #fff !important; color: #000 !important; padding: 0 !important; }
          .ai-contract-draft-page .print-card { background: #fff !important; border: none !important; color: #000 !important; }
        }
      `}</style>
      <div style={S.container}>
        <div className="no-print" style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
          <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, fontSize: 14 }}>
            <ArrowLeft size={18} /> 뒤로
          </button>
          <div style={{ fontSize: 20, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 8 }}>
            <FileText size={18} color="#a78bfa" /> AI 계약서 초안
          </div>
        </div>

        {err && (
          <div className="no-print" style={{ padding: '12px 14px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 8, marginBottom: 16, fontSize: 13, color: '#f87171' }}>
            {err}
          </div>
        )}

        {!extracted && (
          <div className="no-print" style={S.card}>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', lineHeight: 1.7, marginBottom: 18 }}>
              채팅방의 대화 내용을 바탕으로 SW프리랜서 표준계약서 형식의 초안을 자동 생성합니다.
              금액·기간 등 확인이 필요한 항목은 별도로 표시되며, 직접 확인 후 사용해야 합니다.
            </div>
            <AICreditGate toolType="contract" toolLabel="AI 계약서 초안" creditsRemaining={credits}>
              <button onClick={handleGenerate} disabled={generating} style={{
                width: '100%', padding: 14, borderRadius: 10, border: 'none', background: '#a78bfa',
                color: '#fff', fontSize: 15, fontWeight: 700, cursor: generating ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}>
                {generating && <RefreshCw size={15} style={{ animation: 'spin 1s linear infinite' }} />}
                {generating ? GENERATE_STAGES[stageIndex] : '채팅 내용으로 계약서 초안 만들기 (1건 차감)'}
              </button>
            </AICreditGate>
            {generating && (
              <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
            )}
          </div>
        )}

        {extracted && template && (
          <div>
            {extracted.needsReview?.length > 0 && (
              <div className="no-print" style={{ ...S.card, borderColor: 'rgba(251,191,36,0.3)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10, fontSize: 13, fontWeight: 700, color: '#fbbf24' }}>
                  <AlertTriangle size={14} /> 확인이 필요합니다
                </div>
                {extracted.needsReview.map((n, i) => (
                  <div key={i} style={{ fontSize: 13, color: 'rgba(255,255,255,0.65)', padding: '4px 0' }}>- {n}</div>
                ))}
              </div>
            )}

            <div className="print-card" style={S.card}>
              <div style={{ fontSize: 17, fontWeight: 800, marginBottom: 18, textAlign: 'center' }}>{template.title}</div>
              {template.articles.map((a, i) => (
                <div key={i} style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#a78bfa', marginBottom: 4 }}>{a.heading}</div>
                  <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.75)', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{a.body}</div>
                </div>
              ))}
              <div style={{ marginTop: 20, padding: '12px 14px', background: 'rgba(255,255,255,0.04)', borderRadius: 8, fontSize: 11, color: 'rgba(255,255,255,0.45)', lineHeight: 1.6 }}>
                {template.disclaimer}
              </div>
            </div>

            <div className="no-print" style={S.card}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>전자서명이 필요하신가요?</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', lineHeight: 1.7, marginBottom: 12 }}>
                본 초안을 인쇄/저장한 뒤, 외부 전자서명 서비스로 서명을 진행할 수 있습니다.
                전자서명은 계약 체결 사실을 증명할 뿐, 서명 후 대금 미지급 등 불이행까지 막지는 못합니다.
              </div>
              <a href="https://eformsign.com" target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#a78bfa', marginRight: 16 }}>
                <ExternalLink size={12} /> 이폼사인(무료 월 100건)
              </a>
              <a href="https://modusign.co.kr" target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>
                <ExternalLink size={12} /> 모두싸인
              </a>
            </div>

            <div className="no-print" style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button onClick={handleDownload} disabled={downloading} style={{
                flex: 1, minWidth: 140, padding: 13, borderRadius: 10, border: '1px solid #a78bfa',
                background: '#a78bfa', color: '#fff', fontSize: 14, fontWeight: 700, cursor: downloading ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              }}>
                <Download size={15} /> {downloading ? '다운로드 중...' : '워드 파일 다운로드'}
              </button>
              <button onClick={() => window.print()} style={{
                flex: 1, minWidth: 140, padding: 13, borderRadius: 10, border: '1px solid rgba(167,139,250,0.4)',
                background: 'transparent', color: '#a78bfa', fontSize: 14, fontWeight: 700, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              }}>
                <Printer size={15} /> 인쇄 / PDF로 저장
              </button>
              <AICreditGate toolType="contract" toolLabel="AI 계약서 초안" creditsRemaining={credits}>
                <button onClick={handleGenerate} disabled={generating} style={{
                  padding: '13px 18px', borderRadius: 10, border: 'none', background: '#a78bfa',
                  color: '#fff', fontSize: 14, fontWeight: 700, cursor: generating ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, whiteSpace: 'nowrap',
                }}>
                  {generating && <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} />}
                  {generating ? GENERATE_STAGES[stageIndex] : '다시 생성 (1건 차감)'}
                </button>
              </AICreditGate>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIContractDraftPage;
